import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ExternalLink, Volume2, VolumeX } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { api } from '@/api';
import { getYouTubeEmbedUrl, formatDate, resolveMediaUrl } from '@/utils/helpers';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Providers we embed in the in-app movie-theater player
const EMBEDDED_PROVIDERS = new Set(['youtube', 'facebook', 'twitch']);

/**
 * Transform a stream URL into a provider-specific embed URL.
 */
const buildEmbedUrl = (provider, url) => {
  if (!url) return null;
  switch (provider) {
    case 'youtube':
      return getYouTubeEmbedUrl(url);
    case 'facebook': {
      // Facebook video plugin: https://developers.facebook.com/docs/plugins/embedded-video-player
      const encoded = encodeURIComponent(url);
      return `https://www.facebook.com/plugins/video.php?href=${encoded}&show_text=false&autoplay=true`;
    }
    case 'twitch': {
      // Twitch embed needs ?parent= matching the deployed domain.
      const parent = window.location.hostname;
      // Try to extract channel/video from URL
      const m = url.match(/twitch\.tv\/(?:videos\/(\d+)|([^/?]+))/i);
      if (m) {
        if (m[1]) return `https://player.twitch.tv/?video=${m[1]}&parent=${parent}&autoplay=true`;
        if (m[2]) return `https://player.twitch.tv/?channel=${m[2]}&parent=${parent}&autoplay=true`;
      }
      return url;
    }
    default:
      return url;
  }
};

/**
 * Resolve {provider, url} from new stream_provider fields with legacy video_sources fallback.
 */
const resolveStream = (game) => {
  if (game?.stream_provider && game?.stream_url) {
    return { provider: game.stream_provider, url: game.stream_url };
  }
  // Legacy fallback
  const free = game?.video_sources?.find(vs => vs.is_free);
  const any = free || game?.video_sources?.[0];
  if (any) {
    return { provider: any.type === 'uscreen' ? 'uscreen' : (any.type || 'other'), url: any.url };
  }
  return { provider: null, url: null };
};

export const VideoTheater = ({ game, isOpen, onClose }) => {
  const [adState, setAdState] = useState('loading');
  const [adData, setAdData] = useState(null);
  const [countdown, setCountdown] = useState(5);
  const [adTimeRemaining, setAdTimeRemaining] = useState(30);
  const [muted, setMuted] = useState(false);
  const [productionCompany, setProductionCompany] = useState(null);
  const videoRef = useRef(null);
  const timerRef = useRef(null);

  const recordImpression = useCallback(async (adId) => {
    try {
      await fetch(`${BACKEND_URL}/api/ads/impression?ad_id=${adId}&placement=video_player`, { method: 'POST' });
    } catch (error) {
      console.error('Failed to record impression:', error);
    }
  }, []);

  const startAdTimer = useCallback((ad) => {
    const duration = ad.duration_seconds || 30;
    setAdTimeRemaining(duration);
    setCountdown(5);
    timerRef.current = setInterval(() => {
      setAdTimeRemaining(prev => {
        if (prev <= 1) { clearInterval(timerRef.current); setAdState('completed'); return 0; }
        return prev - 1;
      });
      setCountdown(prev => { if (prev <= 1) { setAdState('skippable'); return 0; } return prev - 1; });
    }, 1000);
  }, []);

  const startSponsorTimer = useCallback(() => {
    setAdTimeRemaining(10);
    setCountdown(3);
    timerRef.current = setInterval(() => {
      setAdTimeRemaining(prev => {
        if (prev <= 1) { clearInterval(timerRef.current); setAdState('completed'); return 0; }
        return prev - 1;
      });
      setCountdown(prev => { if (prev <= 1) { setAdState('skippable'); return 0; } return prev - 1; });
    }, 1000);
  }, []);

  // Fetch pre-roll ad + production company when dialog opens
  useEffect(() => {
    const fetchPreRollAd = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/ads/serve/video_player`);
        const data = await response.json();
        if (data.type === 'ad' && data.ad) {
          setAdData(data.ad); setAdState('playing'); startAdTimer(data.ad); recordImpression(data.ad.id);
        } else if (data.type === 'sponsors' && data.sponsors?.length > 0) {
          const sponsor = data.sponsors[Math.floor(Math.random() * data.sponsors.length)];
          setAdData({ id: sponsor.id, image_url: sponsor.logo_url, click_url: sponsor.click_url, company_name: sponsor.company_name, ad_type: 'sponsor' });
          setAdState('playing'); startSponsorTimer();
        } else { setAdState('completed'); }
      } catch (error) { console.error('Failed to fetch pre-roll ad:', error); setAdState('completed'); }
    };
    const fetchProductionCompany = async () => {
      if (!game?.production_company_id) { setProductionCompany(null); return; }
      try {
        const res = await api.getProductionCompany(game.production_company_id);
        setProductionCompany(res.data);
      } catch (err) { setProductionCompany(null); }
    };

    if (isOpen && game) {
      fetchPreRollAd();
      fetchProductionCompany();
    } else {
      setAdState('loading'); setAdData(null); setCountdown(5); setAdTimeRemaining(30);
      setProductionCompany(null);
      if (timerRef.current) clearInterval(timerRef.current);
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [isOpen, game, startAdTimer, startSponsorTimer, recordImpression]);

  const recordClick = async () => {
    if (adData?.id && adData.ad_type !== 'sponsor') {
      try {
        await fetch(`${BACKEND_URL}/api/ads/click?ad_id=${adData.id}`, { method: 'POST' });
      } catch (err) {
        // Click-tracking is best-effort — don't block the user navigating to the sponsor.
        console.warn('VideoTheater: ad click tracking failed (best-effort):', err);
      }
    }
    if (adData?.click_url) window.open(adData.click_url, '_blank');
  };

  const handleSkipAd = () => { if (timerRef.current) clearInterval(timerRef.current); setAdState('completed'); };
  const handleVideoEnded = () => { if (timerRef.current) clearInterval(timerRef.current); setAdState('completed'); };

  if (!game) return null;

  const { provider, url } = resolveStream(game);
  const isEmbedded = provider && EMBEDDED_PROVIDERS.has(provider);
  const embedUrl = isEmbedded ? buildEmbedUrl(provider, url) : null;
  const providerLabel = {
    youtube: 'YouTube', facebook: 'Facebook Live', twitch: 'Twitch',
    hudl: 'Hudl.TV', uscreen: 'Uscreen', veo: 'VEO Live', nationalsoccernetwork: 'National Soccer Network', other: 'External'
  }[provider] || 'Stream';

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl bg-black border-wpsl-blue/40 p-0" data-testid="video-theater">
        <DialogHeader className="p-4 border-b border-wpsl-blue/30 bg-gradient-to-r from-[#001428] via-black to-[#1a0506]">
          <DialogTitle className="text-white" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            {game.home_team_name} vs {game.away_team_name}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Watch {game.home_team_name} vs {game.away_team_name} — {game.league_name || 'WPSL match'} on {formatDate(game.scheduled_time)}.
          </DialogDescription>
          <div className="text-sm text-gray-400 flex items-center gap-2 flex-wrap">
            <span>{game.league_name}</span>
            <span>•</span>
            <span>{formatDate(game.scheduled_time)}</span>
            {productionCompany && (
              <>
                <span>•</span>
                <span className="text-wpsl-blue">
                  Produced by{' '}
                  {productionCompany.website ? (
                    <a
                      href={productionCompany.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline hover:text-white"
                      data-testid="production-company-link"
                    >
                      {productionCompany.name}
                    </a>
                  ) : (
                    <span data-testid="production-company-name">{productionCompany.name}</span>
                  )}
                </span>
              </>
            )}
          </div>
        </DialogHeader>

        <div className="aspect-video w-full bg-black relative">
          {/* Pre-roll Ad */}
          {adState !== 'completed' && adData && (
            <div className="absolute inset-0 z-20 bg-black flex items-center justify-center">
              {adData.video_url ? (
                <video
                  ref={videoRef}
                  src={resolveMediaUrl(adData.video_url)}
                  className="w-full h-full object-contain"
                  autoPlay
                  muted={muted}
                  onEnded={handleVideoEnded}
                  onClick={recordClick}
                  style={{ cursor: adData.click_url ? 'pointer' : 'default' }}
                />
              ) : (
                <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-[#001428] via-black to-[#1a0506] cursor-pointer" onClick={recordClick}>
                  <img src={resolveMediaUrl(adData.image_url)} alt="Advertisement" className="max-w-md max-h-64 object-contain" />
                  {adData.company_name && <p className="text-white text-lg mt-4 font-semibold">Sponsored by {adData.company_name}</p>}
                  {adData.click_url && <p className="text-wpsl-blue text-sm mt-2">Click to learn more</p>}
                </div>
              )}
              <div className="absolute top-4 left-4 bg-black/70 px-3 py-1.5 rounded text-white text-sm font-medium">Ad • {adTimeRemaining}s</div>
              <div className="absolute bottom-4 right-4">
                {adState === 'playing' && countdown > 0 ? (
                  <div className="bg-black/70 px-4 py-2 rounded text-white text-sm">Skip in {countdown}s</div>
                ) : adState === 'skippable' ? (
                  <Button onClick={handleSkipAd} className="bg-white/20 hover:bg-white/30 text-white border border-white/30">Skip Ad →</Button>
                ) : null}
              </div>
              {adData.video_url && (
                <button onClick={() => setMuted(!muted)} className="absolute bottom-4 left-4 bg-black/70 p-2 rounded text-white hover:bg-black/90">
                  {muted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
                </button>
              )}
            </div>
          )}

          {adState === 'loading' && (
            <div className="absolute inset-0 z-20 bg-black flex items-center justify-center">
              <div className="text-white">Loading...</div>
            </div>
          )}

          {/* Main Video Content — embedded provider */}
          {adState === 'completed' && isEmbedded && embedUrl && (
            <iframe
              src={embedUrl}
              className="w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
              allowFullScreen
              title={`${game.home_team_name} vs ${game.away_team_name}`}
              data-testid="embedded-stream-iframe"
            />
          )}

          {/* External provider — redirect call-to-action */}
          {adState === 'completed' && !isEmbedded && url && (
            <div className="w-full h-full flex flex-col items-center justify-center text-center px-6 bg-gradient-to-br from-[#001428] via-black to-[#1a0506]" data-testid="external-stream-cta">
              <div className="w-16 h-16 rounded-full bg-wpsl-red flex items-center justify-center mb-5">
                <ExternalLink className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-white text-2xl font-bold mb-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                This match streams on {providerLabel}
              </h3>
              <p className="text-gray-400 max-w-md mb-6">
                We'll open the stream in a new tab so you can watch on the {providerLabel} platform.
              </p>
              <Button
                onClick={() => window.open(url, '_blank', 'noopener,noreferrer')}
                className="bg-wpsl-red hover:bg-wpsl-blue text-white font-bold rounded-full px-8 py-6 text-base tracking-wide transition-colors"
                style={{ letterSpacing: '0.5px' }}
                data-testid="external-stream-btn"
              >
                Open on {providerLabel}
                <ExternalLink className="w-4 h-4 ml-2" />
              </Button>
            </div>
          )}

          {/* No stream available */}
          {adState === 'completed' && !url && (
            <div className="w-full h-full flex items-center justify-center text-gray-400">
              No video source available
            </div>
          )}
        </div>

        {/* Footer: production credit (also shown in body for visibility) */}
        {productionCompany && (
          <div className="px-4 py-3 bg-black border-t border-wpsl-blue/20 flex items-center justify-between gap-3">
            <div className="text-xs text-gray-400 uppercase tracking-wider">Stream Production</div>
            <div className="flex items-center gap-3">
              {productionCompany.logo && (
                <img
                  src={resolveMediaUrl(productionCompany.logo)}
                  alt={productionCompany.name}
                  className="h-7 w-auto opacity-90"
                />
              )}
              <div className="text-sm text-white font-semibold">{productionCompany.name}</div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default VideoTheater;
